import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { useCurrentOrg } from '../orgs/useCurrentOrg';
import { ConnectorsCatalog } from './ConnectorsCatalog';
import { OAuthCredentialsSection } from './OAuthCredentialsSection';

export function ConnectorsPage() {
  const { org } = useCurrentOrg();
  const orgId = org?.id ?? '';
  const [params, setParams] = useSearchParams();
  const qc = useQueryClient();

  // The OAuth callback redirects back to /connectors with ?connected=<id>
  // or ?error=<code>. Invalidate caches so the new connection appears
  // without a manual refresh, and clear the query string so it doesn't
  // stick around.
  useEffect(() => {
    if (params.get('connected') || params.get('error')) {
      qc.invalidateQueries({ queryKey: ['connections', orgId] });
      const next = new URLSearchParams(params);
      next.delete('connected');
      next.delete('error');
      setParams(next, { replace: true });
    }
  }, [params, orgId, qc, setParams]);

  const error = params.get('error');
  const connected = params.get('connected');

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tightish text-ink-primary">Connectors</h1>
        <p className="mt-1 text-sm text-ink-secondary">
          Curated third-party integrations that your agents can call as tools. Each connection
          uses OAuth credentials you control: Sigil never sees your password, never persists
          plaintext tokens.
        </p>
      </div>

      {connected && (
        <div className="rounded-lg border border-ok/30 bg-ok/5 px-4 py-3 text-sm text-ok">
          Connection added. You can attach it to any agent under its Configuration tab.
        </div>
      )}
      {error && (
        <div className="rounded-lg border border-danger/30 bg-danger/5 px-4 py-3 text-sm text-danger">
          OAuth failed: <code className="font-mono text-xs">{error}</code>
        </div>
      )}

      <OAuthCredentialsSection orgId={orgId} />
      <ConnectorsCatalog orgId={orgId} />
    </div>
  );
}
